import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Kanban, PackageSearch, MapPinned,
  FileText, Settings, LogOut, Sun,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/customers", label: "Clienti", icon: Users, testid: "nav-customers" },
  { to: "/app/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-pipeline" },
  { to: "/app/catalog", label: "Catalogo", icon: PackageSearch, testid: "nav-catalog" },
  { to: "/app/simulator", label: "Simulatore", icon: MapPinned, testid: "nav-simulator", highlight: true },
  { to: "/app/quotes", label: "Preventivi", icon: FileText, testid: "nav-quotes" },
  { to: "/app/settings", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-solar-bg text-foreground">
      <aside className="w-[76px] shrink-0 bg-white sticky top-0 h-screen flex flex-col items-center py-5" data-testid="app-sidebar">
        <Link to="/app/dashboard" className="w-11 h-11 rounded-2xl bg-[#E0A82E] flex items-center justify-center text-zinc-900 mb-7" data-testid="brand-link" title="SolarFlow Pro">
          <Sun size={20} strokeWidth={2.5} />
        </Link>

        <nav className="flex-1 w-full flex flex-col items-stretch justify-center gap-1 pl-3">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                title={n.label}
                aria-label={n.label}
                className={`relative h-12 flex items-center justify-center transition-colors ${
                  active ? "text-zinc-900" : "text-zinc-400 hover:text-zinc-900"
                }`}
              >
                {active && <span aria-hidden className="rail-tab absolute inset-y-1 left-0 right-0" />}
                {!active && <span aria-hidden className="absolute inset-y-1 left-1 right-3 rounded-xl opacity-0 hover:opacity-100 bg-zinc-100 transition-opacity" />}
                <span className="relative z-10 flex items-center justify-center w-11 h-11">
                  <Icon size={19} strokeWidth={2} />
                  {n.highlight && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                </span>
              </Link>
            );
          })}
        </nav>

        <div className="flex flex-col items-center gap-2 mt-2">
          <Link to="/app/settings" className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-white font-semibold text-sm hover:ring-2 hover:ring-zinc-300 transition-all" title={`${user?.name || ""} · Impostazioni`} data-testid="user-name">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </Link>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            data-testid="logout-btn"
            title="Esci"
            aria-label="Esci"
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }) {
  return (
    <div className="px-8 pt-8 pb-6 flex items-end justify-between gap-4 flex-wrap">
      <div>
        {eyebrow && <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">{eyebrow}</div>}
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-zinc-500 mt-1.5 text-sm">{subtitle}</p>}
      </div>
      <div className="flex gap-2">{actions}</div>
    </div>
  );
}
